import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, PageContainer, Tabs } from '@react-apps/design-system';
import type { TabItem } from '@react-apps/design-system';
import type {
  BillingAgreementIntervalUnit,
  DebtArrangementWithRelations,
} from '../../domain/entities/sales.entity';
import { useDebtArrangement } from '../hooks/use-contract-details';
import { EntityEventsSection } from '../components/EntityEventsSection';
import { DebtArrangementReasonModal } from '../components/sales-action-modals';
import { useRouteTabs } from '@/shared/hooks/use-route-tabs';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import { EntityLink } from '@/shared/components/entity-link';
import { FOCUSED_INVOICE_PARAM } from '../shared/invoices';
import {
  DebtArrangementStatusBadge,
  ErrorState,
  Field,
  Mono,
  SectionCard,
  SkeletonCard,
  chargeIntervalLabel,
  formatCurrency,
  formatDateTime,
  isNotFoundError,
} from '../shared/display';

const DEBT_ARRANGEMENT_TAB_IDS = ['details', 'audit'];

/**
 * Página de uma renegociação (Debt Arrangement). Alcançada pelo id na aba
 * "Offer & Renegociações" da transação, não pelo menu. A auditoria da DA mora
 * aqui, numa aba, como nas outras entidades do Sales.
 */
export function DebtArrangementDetailPage() {
  const { debtArrangementId } = useParams<{ debtArrangementId: string }>();
  const id = debtArrangementId ?? '';
  // Mesma query do `DetailsTab`: o React Query não repete a requisição.
  const { data: da } = useDebtArrangement(id);
  const goBack = useBackNavigate(
    da ? `/contracts/${da.contractId}` : '/contracts',
  );
  const { activeTab, onTabChange } = useRouteTabs(
    `/debt-arrangements/${id}`,
    DEBT_ARRANGEMENT_TAB_IDS,
  );

  const tabs: TabItem[] = [
    { id: 'details', label: 'Detalhes', content: <DetailsTab id={id} /> },
    {
      id: 'audit',
      label: 'Auditoria',
      content: (
        <EntityEventsSection entityType="DebtArrangement" entityId={id} />
      ),
    },
  ];

  return (
    <PageContainer maxWidth="4xl">
      <button
        type="button"
        onClick={goBack}
        className="text-(--color-text-secondary) hover:text-(--color-text-primary) focus-visible:ring-(--color-accent) flex items-center gap-1.5 rounded text-sm transition-colors focus-visible:outline-none focus-visible:ring-2"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 19.5 8.25 12l7.5-7.5"
          />
        </svg>
        Voltar
      </button>

      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
    </PageContainer>
  );
}

function DetailsTab({ id }: { id: string }) {
  const { data: da, isLoading, error, refetch } = useDebtArrangement(id);

  if (isLoading) return <SkeletonCard rows={6} />;
  if (error && !isNotFoundError(error))
    return (
      <ErrorState
        message="Não foi possível carregar a renegociação."
        onRetry={() => void refetch()}
      />
    );
  if (!da) return <ErrorState message="Renegociação não encontrada." />;

  const snapshot = da.offerSnapshot;
  return (
    <div className="space-y-6">
      <SectionCard
        title="Renegociação"
        action={<DebtArrangementActions da={da} />}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="ID" value={<Mono>{da.id}</Mono>} />
          <Field
            label="Status"
            value={<DebtArrangementStatusBadge status={da.status} />}
          />
          <Field label="Tipo" value={da.type} />
          <Field label="Motivo" value={da.reason} />
          <Field
            label="Contrato"
            value={
              <Mono>
                <EntityLink kind="contract" id={da.contractId} />
              </Mono>
            }
          />
          <Field
            label="Offer externa"
            value={
              da.externalOfferId ? <Mono>{da.externalOfferId}</Mono> : null
            }
          />
          <Field
            label="BA substituída"
            value={
              <Mono>
                <EntityLink
                  kind="billingAgreement"
                  id={da.replacedBillingAgreementId}
                />
              </Mono>
            }
          />
          <Field
            label="BA resultante"
            value={
              <Mono>
                <EntityLink
                  kind="billingAgreement"
                  id={da.resultingBillingAgreementId}
                />
              </Mono>
            }
          />
          <Field label="Criada em" value={formatDateTime(da.createdAt)} />
          <Field label="Atualizada em" value={formatDateTime(da.updatedAt)} />
        </div>
      </SectionCard>

      <SectionCard title="Faturas de origem">
        {da.sourceInvoiceIds.length === 0 ? (
          <p className="text-(--color-text-secondary) text-sm">
            Nenhuma fatura de origem.
          </p>
        ) : (
          // Fatura não tem página: o link abre o resumo dela na BA substituída.
          <ul className="space-y-1">
            {da.sourceInvoiceIds.map((invoiceId) => (
              <li key={invoiceId}>
                <Mono>
                  <Link
                    to={`/billing-agreements/${da.replacedBillingAgreementId}?${FOCUSED_INVOICE_PARAM}=${invoiceId}`}
                    className="text-(--color-accent) hover:underline"
                  >
                    {invoiceId}
                  </Link>
                </Mono>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <SectionCard title="Oferta da renegociação">
        {snapshot ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Ciclos" value={snapshot.cycles} />
            <Field
              label="Valor por ciclo"
              value={formatCurrency(snapshot.value)}
            />
            <Field label="Moeda" value={snapshot.currency} />
            <Field
              label="Intervalo"
              value={chargeIntervalLabel(
                snapshot.interval,
                snapshot.intervalType as BillingAgreementIntervalUnit | null,
              )}
            />
          </div>
        ) : (
          <p className="text-(--color-text-secondary) text-sm">
            Capturada ao concluir o rascunho.
          </p>
        )}
      </SectionCard>
    </div>
  );
}

/**
 * As mesmas ações da lista da aba "Offer & Renegociações". Editar e concluir o
 * rascunho dependem da BA, da cadeia e das faturas (`RenegotiationModal`), que
 * já moram na página da BA substituída: aqui, só o link.
 */
function DebtArrangementActions({ da }: { da: DebtArrangementWithRelations }) {
  const [action, setAction] = useState<'cancel' | 'revert' | null>(null);
  const canRevert = da.status === 'waiting_payment' || da.status === 'active';
  const isDraft = da.status === 'draft';
  if (!canRevert && !isDraft) return null;

  return (
    <div className="flex items-center gap-2">
      {isDraft && (
        <>
          <Link
            to={`/billing-agreements/${da.replacedBillingAgreementId}`}
            className="text-(--color-accent) text-sm font-medium hover:underline"
          >
            Editar na BA
          </Link>
          <Button variant="danger" onClick={() => setAction('cancel')}>
            Cancelar rascunho
          </Button>
        </>
      )}
      {canRevert && (
        <Button variant="danger" onClick={() => setAction('revert')}>
          Reverter
        </Button>
      )}
      {action && (
        <DebtArrangementReasonModal
          invalidateKey={['sales']}
          debtArrangementId={da.id}
          action={action}
          open
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}
